/**
 * The scheduler facade - the only part of `src/scheduler/` the UI is allowed to
 * know about.
 *
 * Everything below this file is a pure step in a pipeline: eligibility decides
 * who is owed a break, scoring decides what a slot is worth, the solver arranges
 * them, coverage measures the damage and validate.js writes the complaints. None
 * of those modules knows how to assemble a week, and none of them should - a
 * component that had to call six of them in the right order would end up with
 * its own private, subtly different idea of what a schedule is.
 *
 * So this module owns exactly three responsibilities:
 *
 *  1. **Building the DayContext.** `buildContext` is the single definition of
 *     what one day's inputs are. The solver and the scorer both read it, so a
 *     field missing here is a silent behaviour change several modules away.
 *  2. **Sequencing a week.** Days are always processed in calendar order because
 *     the fairness and history carry-overs are week-level: who got their ideal
 *     slot on Monday changes who wins the tie on Tuesday.
 *  3. **Applying an edit without re-solving.** Dragging one lunch must move one
 *     lunch. If a manual placement quietly re-ran the solver, everybody else's
 *     time could change underneath the manager and the app would be untrustworthy
 *     in exactly the moment it is being corrected.
 *
 * Nothing handed in is ever mutated: every entry point returns a fresh Schedule
 * built from copies, so the store can diff old against new and a caller can keep
 * the previous version for undo.
 *
 * Pure logic: no DOM, no storage, no timers. The one impurity is
 * `new Date().toISOString()` for `generatedAt`, which is a fact about the run
 * rather than an input to it.
 */

import {
  DAY_KEYS,
  dateForDayKey,
  isWeekend,
  parseTimeToMinutes,
} from '../core/time.js';
import {
  DEFAULT_SETTINGS,
  EVENING_SINGLE_LUNCH_FROM_MINUTES,
  NISCC_SINGLE_LUNCH_CUTOFF_MINUTES,
  resolveDayConfig,
} from '../core/settings.js';
import {
  ASSIGNMENT_SOURCES,
  buildDayShifts,
  buildWeekShifts,
  createAssignment,
  createSchedule,
  indexStaffRules,
  workingShifts,
} from '../core/model.js';
import { assessDay } from './eligibility.js';
import { computeCoverage, timelineRange } from './coverage.js';
import { solveDay } from './solver.js';
import { explainAssignment } from './explain.js';
import { validateWeek } from './validate.js';

/** Bump when a saved schedule must be rebuilt to apply a new scheduling rule. */
export const SCHEDULE_RULES_VERSION = 6;

/**
 * Breach codes as written by `coverage.js`. Mirrored rather than imported
 * because that module deliberately exports only the four functions the contract
 * lists; {@link rebuildCoverageBuckets} is the one place they are needed here.
 */
const BREACH_BELOW_MINIMUM = 'below-minimum';
const BREACH_TOO_MANY_SIMULTANEOUS = 'too-many-simultaneous';

/* -------------------------------------------------------------------------- */
/* Context                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Build everything one day's solve needs, in one object.
 *
 * This is the integration point of the whole scheduler, so it is deliberately
 * exhaustive rather than minimal: `solver.js` and `scoring.js` both fall back to
 * deriving a missing field, and a context that leaves them to it would work
 * while quietly doing the derivation twice per candidate - and, worse, would let
 * the two modules disagree about the day config they were using.
 *
 * The returned fields and who reads them:
 *
 *  - `dayKey` / `date` - solver (assignment stamping), UI.
 *  - `settings` / `dayConfig` - every hard rule and every weight.
 *  - `rulesIndex` - per-employee overrides; also handed to validate.js.
 *  - `shifts` - every rota row for the day, blanks and absences included, so the
 *    UI can say why somebody is not on the list.
 *  - `working` - the `kind === 'shift'` subset. Coverage maths only. Pre-filtered
 *    because the scorer samples it for every instant of every candidate.
 *  - `entries` - one EligibilityResult per row, in rota order. The solver filters
 *    it for eligibility; the `startOrder` and `fairness` terms read the cohort.
 *  - `eligibility` - the same results indexed by employee id, for the O(1)
 *    lookups the facade and the UI need when reasoning about one person.
 *  - `timeline` - the render/bucket window, so the coverage grid stored on the
 *    day matches the axis the timeline view draws.
 *  - `lockedAssignments` - copies, never the stored objects: `solver.js` returns
 *    the locked records it was given straight through into its result, and
 *    passing the originals would splice the previous schedule into the new one.
 *  - `fairness` - running count of ideal slots already won this week.
 *  - `history` - lunches already placed this week, per employee.
 *
 * @param {object} input
 * @param {object} input.rota The week's Rota. Only read when `shifts` is absent.
 * @param {string} input.dayKey 'mon'..'sun'.
 * @param {object} [input.settings] Resolved Settings; defaults are used when absent.
 * @param {object[]|Map} [input.staffRules] StaffRule array, or an already-built index.
 * @param {object[]} [input.lockedAssignments] Assignments to seed as immovable.
 * @param {object[]} [input.shifts] Pre-built ShiftRecords, to avoid re-parsing the
 *   rota once per day when a caller is walking the whole week.
 * @param {Map|object} [input.fairness] Carry-over ideal-slot counts.
 * @param {Map|object} [input.history] Carry-over placements, keyed by employee id.
 * @returns {object} DayContext.
 */
export function buildContext({
  rota,
  dayKey,
  settings,
  staffRules,
  lockedAssignments,
  shifts,
  fairness,
  history,
} = {}) {
  const resolvedSettings = settings ?? DEFAULT_SETTINGS;
  const rulesIndex = toRulesIndex(staffRules);
  const dayConfig = resolveDayConfig(resolvedSettings, dayKey);

  const dayShifts = Array.isArray(shifts) ? shifts : buildDayShifts(rota, dayKey, resolvedSettings);
  const working = workingShifts(dayShifts);
  const entries = assessDay(dayShifts, { settings: resolvedSettings, dayConfig, rulesIndex });

  const eligibility = new Map();
  for (const entry of entries) {
    if (entry.employeeId) eligibility.set(entry.employeeId, entry);
  }

  return {
    dayKey,
    date: rota?.weekCommencing ? dateForDayKey(rota.weekCommencing, dayKey) : '',
    weekCommencing: rota?.weekCommencing ?? '',
    settings: resolvedSettings,
    dayConfig,
    rulesIndex,
    shifts: dayShifts,
    working,
    entries,
    eligibility,
    timeline: timelineRange(dayShifts, resolvedSettings),
    // Only placed locks can be honoured; an unplaced one would seed the solver
    // with an occupancy it cannot describe.
    lockedAssignments: asArray(lockedAssignments)
      .filter((assignment) => assignment?.employeeId && Number.isFinite(assignment.slotMinutes))
      .map((assignment) => cloneAssignment(assignment)),
    fairness: toCountMap(fairness),
    history: toListMap(history),
  };
}

/* -------------------------------------------------------------------------- */
/* Generation                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Generate (or regenerate) a week.
 *
 * Days are always walked in calendar order even when only one is being
 * regenerated, because the fairness and history carry-overs make a day's inputs
 * depend on the days before it. A day outside `options.days` is copied out of
 * `previousSchedule` verbatim - assignments, unassigned records, coverage and
 * solved flag - and still contributes its placements to those carry-overs, so
 * regenerating Wednesday cannot silently reset Monday's fairness ledger.
 *
 * Two carry-over rules are worth stating plainly because they surprise people:
 *
 *  - **Locked assignments survive a regenerate.** They are read out of
 *    `previousSchedule` for every regenerated day and seeded into the solver as
 *    immovable, so a manager's pinned decision is honoured rather than re-argued.
 *  - **Manual but unlocked assignments do not.** A full regenerate is a request
 *    for the rules' answer; a hand placement that has not been locked is treated
 *    as a draft. Lock it if you want it kept - that is the whole point of the
 *    lock, and silently preserving unlocked edits would make "regenerate" mean
 *    something different every time.
 *
 * Validation runs once over the finished week rather than per day, because
 * duplicate rota rows, unresolved aliases and import confidence are week-level
 * facts. Warnings from `previousSchedule` are seeded onto the draft first so
 * that `validateWeek` can see which ids a manager had already acknowledged and
 * carry those acknowledgements across the regeneration.
 *
 * @param {object} input
 * @param {object} input.rota The week's Rota.
 * @param {object} [input.settings] Resolved Settings.
 * @param {object[]|Map} [input.staffRules] StaffRules, or an index of them.
 * @param {object} [input.aliasBook] AliasBook; only validation reads it.
 * @param {object} [input.previousSchedule] The schedule being replaced.
 * @param {{ days?: string[] }} [input.options] Limit regeneration to these days.
 * @returns {object} A brand-new Schedule. The inputs are not mutated.
 */
export function generateSchedule({
  rota,
  settings,
  staffRules,
  aliasBook,
  previousSchedule,
  options,
} = {}) {
  const resolvedSettings = settings ?? DEFAULT_SETTINGS;
  const rulesIndex = toRulesIndex(staffRules);
  const weekCommencing = rota?.weekCommencing ?? previousSchedule?.weekCommencing ?? '';
  const weekShifts = buildWeekShifts(rota, resolvedSettings);
  const regenerating = resolveTargetDays(options?.days, previousSchedule);

  const schedule = createSchedule(weekCommencing);
  schedule.rulesVersion = SCHEDULE_RULES_VERSION;
  schedule.generatedAt = new Date().toISOString();

  // Week-level ledgers, filled as the days are walked so that a later day sees
  // what the earlier ones did.
  const fairness = new Map();
  const history = new Map();
  let eligible = 0;

  for (const dayKey of DAY_KEYS) {
    const dayShifts = weekShifts[dayKey] ?? [];
    const previousDay = previousSchedule?.days?.[dayKey] ?? null;
    const day = schedule.days[dayKey];

    const context = buildContext({
      rota,
      dayKey,
      settings: resolvedSettings,
      staffRules: rulesIndex,
      // Only a regenerated day needs its locks seeding; a copied day already
      // carries them in the assignments it is being copied from.
      lockedAssignments: regenerating.has(dayKey) ? lockedIn(previousDay) : [],
      shifts: dayShifts,
      fairness,
      history,
    });
    eligible += context.entries.filter((entry) => entry.eligible).length;

    if (regenerating.has(dayKey)) {
      const result = solveDay(context);
      day.assignments = result.assignments;
      day.unassigned = result.unassigned;
      day.solved = result.solved;
      day.coverage = computeCoverage({
        shifts: context.shifts,
        assignments: day.assignments,
        dayConfig: context.dayConfig,
        settings: resolvedSettings,
        range: context.timeline,
      });
      // Carried purely so acknowledgements survive; validateWeek replaces them.
      day.warnings = cloneWarnings(previousDay?.warnings);
    } else if (previousDay) {
      day.assignments = asArray(previousDay.assignments).map((item) => cloneAssignment(item));
      day.unassigned = asArray(previousDay.unassigned).map((item) => ({ ...item }));
      day.coverage = cloneCoverage(previousDay.coverage);
      day.warnings = cloneWarnings(previousDay.warnings);
      day.solved = previousDay.solved !== false;
    }

    recordWeekLedgers(day, context, fairness, history);
  }

  schedule.warnings = cloneWarnings(previousSchedule?.warnings);
  applyValidation(schedule, { rota, weekShifts, settings: resolvedSettings, rulesIndex, aliasBook });
  schedule.stats = buildStats(schedule, eligible);

  return schedule;
}

/**
 * True when a persisted schedule predates the current scheduler rules or
 * violates a current non-negotiable rule.
 *
 * Old schedules are valid data, so storage keeps them. At startup the UI uses
 * this narrow audit to decide whether the current week must be regenerated.
 * The rules-version marker handles one-time changes such as the long-day lunch
 * preference; the direct audit also catches weekday overlap before 13:00
 * (NISCC), or an assignment outside a confirmed employee clock-time window.
 */
export function scheduleViolatesCurrentHardRules(schedule, { settings, staffRules } = {}) {
  if (!schedule?.days) return false;
  if (schedule.rulesVersion !== SCHEDULE_RULES_VERSION) return true;
  const lunchMinutes = positive(
    settings?.lunchDurationMinutes,
    DEFAULT_SETTINGS.lunchDurationMinutes,
  );
  const rulesIndex = toRulesIndex(staffRules);

  for (const dayKey of DAY_KEYS) {
    const assignments = asArray(schedule.days?.[dayKey]?.assignments)
      .filter((assignment) => Number.isFinite(assignment?.slotMinutes));

    for (const assignment of assignments) {
      const rule = rulesIndex.get(assignment.employeeId);
      const earliest = parseTimeToMinutes(rule?.earliestLunchTime);
      const latest = parseTimeToMinutes(rule?.latestLunchTime);
      if (Number.isFinite(earliest) && assignment.slotMinutes < earliest) return true;
      if (Number.isFinite(latest) && assignment.slotMinutes > latest) return true;
    }

    // Two overlapping lunches are a hard-rule breach in two windows: the NISCC
    // weekday morning, and every evening from 17:00. The evening rule applies at
    // weekends too, so it is checked before the weekday-only guard below.
    for (let left = 0; left < assignments.length; left += 1) {
      const first = assignments[left];
      const firstEnd = first.slotMinutes + positive(first.durationMinutes, lunchMinutes);
      for (let right = left + 1; right < assignments.length; right += 1) {
        const second = assignments[right];
        const overlapStart = Math.max(first.slotMinutes, second.slotMinutes);
        const overlapEnd = Math.min(
          firstEnd,
          second.slotMinutes + positive(second.durationMinutes, lunchMinutes),
        );
        if (overlapStart >= overlapEnd) continue;
        if (overlapStart >= EVENING_SINGLE_LUNCH_FROM_MINUTES) return true;
        if (!isWeekend(dayKey) && overlapStart < NISCC_SINGLE_LUNCH_CUTOFF_MINUTES) return true;
      }
    }
  }

  return false;
}

/**
 * Regenerate one day, leaving the rest of the week exactly as it is.
 *
 * A thin wrapper over {@link generateSchedule} on purpose: "regenerate Tuesday"
 * and "regenerate everything" must not be two implementations that can drift
 * apart, and the carry-over ledgers only mean anything when the whole week is
 * walked in order.
 *
 * @param {object} schedule The current Schedule.
 * @param {string} dayKey The day to redo.
 * @param {{ rota: object, settings?: object, staffRules?: object[]|Map, aliasBook?: object }} deps
 * @returns {object} A new Schedule.
 */
export function regenerateDay(schedule, dayKey, deps = {}) {
  return generateSchedule({
    rota: deps.rota,
    settings: deps.settings,
    staffRules: deps.staffRules,
    aliasBook: deps.aliasBook,
    previousSchedule: schedule,
    options: { days: [dayKey] },
  });
}

/* -------------------------------------------------------------------------- */
/* Edits                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Place or move one lunch by hand.
 *
 * **This never re-solves the day.** Moving one person must move one person:
 * a manager who drags Josh to 14:00 and finds that Emma and Cathy have also
 * shuffled has lost the thread of what the app is doing, and will go back to a
 * spreadsheet. Coverage and warnings are recomputed so the consequences are
 * visible immediately - but the consequences are reported, not corrected.
 *
 * The assignment is stamped `manual: true` / `source: 'manual'` and its
 * explanation is rebuilt as a manual placement: no score, no factors, and a
 * summary that says the time was chosen by hand. Attaching solver reasoning to a
 * human decision would be an invention, and it is exactly the kind of invention
 * that destroys trust in the explain panel.
 *
 * `locked` is left alone. Dragging a lunch is not the same act as pinning it,
 * and a drag that silently locked would make the next regenerate behave
 * differently for reasons the manager never chose.
 *
 * @param {object} schedule The current Schedule.
 * @param {object} input
 * @param {string} input.dayKey Day to edit.
 * @param {string} input.employeeId Who is being placed or moved.
 * @param {number} input.slotMinutes New slot start, minutes from midnight.
 * @param {object} [input.deps] `{ rota, settings, staffRules, aliasBook }`.
 * @returns {object} A new Schedule.
 */
export function applyManualAssignment(schedule, { dayKey, employeeId, slotMinutes, deps } = {}) {
  return editDay(schedule, dayKey, deps, (day, context) => {
    if (!employeeId || !Number.isFinite(slotMinutes)) return;

    const entry = context?.eligibility?.get(employeeId) ?? null;
    const settings = context?.settings ?? deps?.settings ?? DEFAULT_SETTINGS;
    const duration = positive(settings.lunchDurationMinutes, DEFAULT_SETTINGS.lunchDurationMinutes);
    const existing = day.assignments.find((item) => item.employeeId === employeeId) ?? null;

    const assignment = existing ?? createAssignment({
      employeeId,
      name: entry?.name ?? '',
      dayKey,
      group: entry?.group ?? 'day',
      durationMinutes: duration,
    });

    assignment.slotMinutes = slotMinutes;
    assignment.durationMinutes = positive(assignment.durationMinutes, duration);
    assignment.manual = true;
    assignment.source = ASSIGNMENT_SOURCES.MANUAL;
    // A solver score describes a slot the solver picked; this one is not it.
    assignment.score = null;
    if (entry?.name) assignment.name = entry.name;
    if (entry?.group) assignment.group = entry.group;

    assignment.explanation = explainAssignment({
      entry,
      assignment,
      breakdown: null,
      alternatives: [],
      context,
    });

    if (!existing) day.assignments.push(assignment);
    day.assignments.sort(compareAssignments);
  });
}

/**
 * Remove one person's lunch from one day.
 *
 * Coverage and validation are recomputed - removing a break is how an "eligible
 * but no lunch" warning appears, and a manager needs to see that immediately
 * rather than after the next regenerate. Nothing else on the day is touched:
 * the freed slot is not offered to anybody.
 *
 * @param {object} schedule The current Schedule.
 * @param {string} dayKey Day to edit.
 * @param {string} employeeId Whose lunch to drop.
 * @param {object} [deps] `{ rota, settings, staffRules, aliasBook }`. Optional, but
 *   without a rota the warnings cannot be re-derived and are left as they were.
 * @returns {object} A new Schedule.
 */
export function removeAssignment(schedule, dayKey, employeeId, deps = undefined) {
  return editDay(schedule, dayKey, deps, (day) => {
    day.assignments = day.assignments.filter((item) => item.employeeId !== employeeId);
  });
}

/**
 * Pin or unpin one lunch.
 *
 * A lock is the manager's promise to themselves that the next regenerate will
 * not touch this time, so it is stored on the assignment and read back by
 * {@link generateSchedule}. Coverage and validation are still refreshed, for one
 * reason only: this function must be indistinguishable in discipline from the
 * other two, so that no caller ever has to remember which edits leave the
 * schedule's derived data stale.
 *
 * @param {object} schedule The current Schedule.
 * @param {string} dayKey Day to edit.
 * @param {string} employeeId Whose lunch to pin.
 * @param {boolean} locked Desired lock state.
 * @param {object} [deps] `{ rota, settings, staffRules, aliasBook }`.
 * @returns {object} A new Schedule.
 */
export function toggleLock(schedule, dayKey, employeeId, locked, deps = undefined) {
  return editDay(schedule, dayKey, deps, (day) => {
    for (const assignment of day.assignments) {
      if (assignment.employeeId === employeeId) assignment.locked = locked === true;
    }
  });
}

/**
 * Record that a manager has seen a warning and meant it anyway.
 *
 * The acknowledgement is written in two places on purpose. On the warning it
 * silences the complaint for this schedule; on the *assignment* it is durable - 
 * `validate.js` rebuilds warning ids from `code:dayKey:employeeId:slotMinutes`
 * and reads `acknowledgedWarnings` back off assignments, so an override survives
 * a regenerate as long as the lunch itself does. Josh's Friday 14:00 lunch,
 * one hour into a 13:00 start, is the case this exists for: a real decision that
 * breaks a real rule, which should be visible once and then stay quiet.
 *
 * @param {object} schedule The current Schedule.
 * @param {string} warningId Deterministic id from `validateWeek`.
 * @param {string} reason The manager's justification. Stored verbatim.
 * @returns {object} A new Schedule.
 */
export function acknowledgeWarning(schedule, warningId, reason) {
  if (!schedule || !warningId) return schedule;

  const next = cloneSchedule(schedule);
  const text = String(reason ?? '').trim();
  let target = null;

  const accept = (warning) => {
    if (warning.id !== warningId) return warning;
    target = warning;
    return { ...warning, acknowledged: true, acknowledgedReason: text };
  };

  next.warnings = next.warnings.map(accept);
  for (const dayKey of DAY_KEYS) {
    const day = next.days[dayKey];
    if (day) day.warnings = day.warnings.map(accept);
  }

  if (!target) return schedule;

  // Stamp the assignment the complaint is about, so the override outlives this
  // particular warning object.
  const day = target.dayKey ? next.days[target.dayKey] : null;
  if (day && target.employeeId) {
    for (const assignment of day.assignments) {
      if (assignment.employeeId !== target.employeeId) continue;
      assignment.overrideReason = text;
      assignment.acknowledgedWarnings = assignment.acknowledgedWarnings.includes(target.code)
        ? assignment.acknowledgedWarnings
        : [...assignment.acknowledgedWarnings, target.code];
    }
  }

  return next;
}

/* -------------------------------------------------------------------------- */
/* Shared edit machinery                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The common shape of every single-day edit: copy the schedule, change one day,
 * then bring the derived data back into step.
 *
 * Coverage is always recomputed, because it is the number the manager is about
 * to act on. Validation is re-run whenever a rota is available; without one the
 * existing warnings are left untouched rather than rebuilt from a rota-less
 * schedule, which would report every employee as "not on the rota" and turn a
 * missing dependency into a screenful of nonsense.
 */
function editDay(schedule, dayKey, deps, mutate) {
  if (!schedule?.days?.[dayKey]) return schedule;

  const settings = deps?.settings ?? DEFAULT_SETTINGS;
  const rulesIndex = toRulesIndex(deps?.staffRules);
  const next = cloneSchedule(schedule);
  const day = next.days[dayKey];

  const context = deps?.rota
    ? buildContext({
      rota: deps.rota,
      dayKey,
      settings,
      staffRules: rulesIndex,
      lockedAssignments: lockedIn(day),
    })
    : null;

  mutate(day, context);

  day.coverage = context
    ? computeCoverage({
      shifts: context.shifts,
      assignments: day.assignments,
      dayConfig: context.dayConfig,
      settings,
      range: context.timeline,
    })
    // No rota to hand: the shifts have not changed, so the existing buckets
    // still describe who is on duty and only the lunch figures need redoing.
    : rebuildCoverageBuckets(day.coverage, day.assignments, settings);

  if (deps?.rota) {
    applyValidation(next, {
      rota: deps.rota,
      settings,
      rulesIndex,
      aliasBook: deps.aliasBook,
    });
    next.stats = buildStats(next, countEligible(deps.rota, settings, rulesIndex));
  } else {
    next.stats = buildStats(next, schedule.stats?.eligible ?? 0);
  }

  return next;
}

/**
 * Run the warnings engine over the finished week and file the results.
 *
 * Week-scoped warnings (`dayKey: null`) go on the schedule and day-scoped ones
 * on their day, each exactly once - `validateWeek` already indexes them that
 * way, and duplicating a week-level complaint onto all seven day cards would
 * make one problem look like seven.
 */
function applyValidation(schedule, { rota, weekShifts, settings, rulesIndex, aliasBook }) {
  const result = validateWeek({
    rota,
    weekShifts,
    schedule,
    settings,
    rulesIndex,
    aliasBook,
  });

  schedule.warnings = result.warnings.filter((warning) => warning.dayKey === null);
  for (const dayKey of DAY_KEYS) {
    if (schedule.days[dayKey]) schedule.days[dayKey].warnings = result.byDay[dayKey] ?? [];
  }
  return result;
}

/**
 * Update the week-level ledgers with what a finished day actually did.
 *
 * "Well placed" means landing on exactly the slot the entry asked for - the same
 * test `solver.js` and the `fairness` term in `scoring.js` use. If this counted
 * anything looser the ledger would stop meaning what those two think it means
 * and the fairness term would quietly stop working.
 */
function recordWeekLedgers(day, context, fairness, history) {
  for (const assignment of day.assignments) {
    const entry = context.eligibility.get(assignment.employeeId) ?? null;
    if (entry && Number.isFinite(entry.preferredMinutes)
      && assignment.slotMinutes === entry.preferredMinutes) {
      fairness.set(assignment.employeeId, (fairness.get(assignment.employeeId) ?? 0) + 1);
    }

    const placements = history.get(assignment.employeeId) ?? [];
    placements.push({
      dayKey: day.dayKey,
      slotMinutes: assignment.slotMinutes,
      group: assignment.group,
    });
    history.set(assignment.employeeId, placements);
  }
}

/**
 * Week totals for the header.
 *
 * `eligible` is counted from the rules rather than from the schedule, so
 * `eligible - assigned` is the number of people the week has let down - which is
 * the only reason the figure is on screen at all.
 */
function buildStats(schedule, eligible) {
  let assigned = 0;
  let unassigned = 0;
  let locked = 0;
  let manual = 0;
  let daysUnsolved = 0;

  for (const dayKey of DAY_KEYS) {
    const day = schedule.days[dayKey];
    if (!day) continue;
    assigned += day.assignments.length;
    unassigned += day.unassigned.length;
    for (const assignment of day.assignments) {
      if (assignment.locked === true) locked += 1;
      if (assignment.manual === true) manual += 1;
    }
    if (day.solved === false) daysUnsolved += 1;
  }

  return { eligible, assigned, unassigned, locked, manual, daysUnsolved };
}

/** How many lunches the rules say the week owes, independently of the schedule. */
function countEligible(rota, settings, rulesIndex) {
  const weekShifts = buildWeekShifts(rota, settings);
  let total = 0;
  for (const dayKey of DAY_KEYS) {
    const dayConfig = resolveDayConfig(settings, dayKey);
    const entries = assessDay(weekShifts[dayKey] ?? [], { settings, dayConfig, rulesIndex });
    total += entries.filter((entry) => entry.eligible).length;
  }
  return total;
}

/* -------------------------------------------------------------------------- */
/* Coverage without a rota                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Recompute the lunch half of a coverage grid in place of a full rebuild.
 *
 * An edit changes who is at lunch; it never changes who is rostered. So the
 * bucket boundaries, the head count and the limits are all still correct and
 * only `onLunch`, `remaining` and the breaches need redoing. That makes an edit
 * without a rota exact rather than approximate - the alternative would be
 * leaving a stale grid on screen beside a warning that contradicts it.
 */
function rebuildCoverageBuckets(buckets, assignments, settings) {
  const duration = positive(settings?.lunchDurationMinutes, DEFAULT_SETTINGS.lunchDurationMinutes);

  return asArray(buckets).map((bucket) => {
    const lunchEmployeeIds = assignments
      .filter((assignment) => Number.isFinite(assignment?.slotMinutes)
        && assignment.slotMinutes < bucket.endMinutes
        && bucket.startMinutes < assignment.slotMinutes + duration)
      .map((assignment) => assignment.employeeId);

    const onLunch = lunchEmployeeIds.length;
    const remaining = number(bucket.scheduled, 0) - onLunch;
    const breaches = [];
    if (number(bucket.scheduled, 0) > 0 || onLunch > 0) {
      if (remaining < number(bucket.minRequired, 0)) breaches.push(BREACH_BELOW_MINIMUM);
      if (onLunch > number(bucket.maxSimultaneous, 1)) breaches.push(BREACH_TOO_MANY_SIMULTANEOUS);
    }

    return {
      ...bucket,
      employeeIds: asArray(bucket.employeeIds).slice(),
      lunchEmployeeIds,
      onLunch,
      remaining,
      breaches,
      ok: breaches.length === 0,
    };
  });
}

/* -------------------------------------------------------------------------- */
/* Copying                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Copy a Schedule deeply enough that no edit can reach the original.
 *
 * Every array a caller might push to or sort is duplicated. `explanation` is
 * shared by reference: it is a finished record of a decision that already
 * happened, is never edited in place, and is by far the largest thing on an
 * assignment.
 */
function cloneSchedule(schedule) {
  const days = {};
  for (const dayKey of DAY_KEYS) {
    const day = schedule?.days?.[dayKey];
    days[dayKey] = day
      ? {
        ...day,
        assignments: asArray(day.assignments).map((item) => cloneAssignment(item)),
        unassigned: asArray(day.unassigned).map((item) => ({ ...item })),
        coverage: cloneCoverage(day.coverage),
        warnings: cloneWarnings(day.warnings),
      }
      : {
        dayKey,
        date: '',
        assignments: [],
        unassigned: [],
        coverage: [],
        warnings: [],
        solved: true,
      };
  }

  return {
    ...schedule,
    days,
    warnings: cloneWarnings(schedule?.warnings),
    stats: schedule?.stats ? { ...schedule.stats } : null,
  };
}

function cloneAssignment(assignment) {
  return {
    ...assignment,
    acknowledgedWarnings: asArray(assignment?.acknowledgedWarnings).slice(),
  };
}

function cloneCoverage(buckets) {
  return asArray(buckets).map((bucket) => ({
    ...bucket,
    breaches: asArray(bucket.breaches).slice(),
    employeeIds: asArray(bucket.employeeIds).slice(),
    lunchEmployeeIds: asArray(bucket.lunchEmployeeIds).slice(),
  }));
}

function cloneWarnings(warnings) {
  return asArray(warnings).map((warning) => ({ ...warning }));
}

/* -------------------------------------------------------------------------- */
/* Small shared helpers                                                       */
/* -------------------------------------------------------------------------- */

/** The locked assignments on a DaySchedule, or nothing when there is no day. */
function lockedIn(day) {
  return asArray(day?.assignments).filter((assignment) => assignment?.locked === true);
}

/**
 * Which days to regenerate.
 *
 * No list at all means the whole week. An empty or entirely unrecognised list
 * means *no* day - a caller who built `days` from a filter that matched nothing
 * gets a faithful copy of the week rather than having every unlocked manual
 * placement in it silently thrown away. The one exception is a first
 * generation: with no previous schedule to copy there is nothing to protect and
 * "regenerate nothing" would return an empty week, so the whole week is solved.
 */
function resolveTargetDays(days, previousSchedule) {
  if (!Array.isArray(days)) return new Set(DAY_KEYS);
  const wanted = days.filter((dayKey) => DAY_KEYS.includes(dayKey));
  if (wanted.length > 0) return new Set(wanted);
  return previousSchedule ? new Set() : new Set(DAY_KEYS);
}

/** Accept either a StaffRule array or an already-built index. */
function toRulesIndex(staffRules) {
  if (staffRules instanceof Map) return staffRules;
  return indexStaffRules(asArray(staffRules));
}

/** Coerce a carry-over counter into the Map the solver seeds its state from. */
function toCountMap(source) {
  if (source instanceof Map) return source;
  const map = new Map();
  if (Array.isArray(source)) {
    for (const [key, value] of source) map.set(key, number(value, 0));
  } else if (source && typeof source === 'object') {
    for (const key of Object.keys(source)) map.set(key, number(source[key], 0));
  }
  return map;
}

/** Same, for the placement history: employee id -> list of placements. */
function toListMap(source) {
  if (source instanceof Map) return source;
  const map = new Map();
  if (source && typeof source === 'object') {
    for (const key of Object.keys(source)) map.set(key, asArray(source[key]).slice());
  }
  return map;
}

/** Output order: by time, then by name, so a day reads down the clock. */
function compareAssignments(a, b) {
  const bySlot = number(a?.slotMinutes, 0) - number(b?.slotMinutes, 0);
  if (bySlot !== 0) return bySlot;
  const byName = String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'en-GB');
  if (byName !== 0) return byName;
  return String(a?.employeeId ?? '').localeCompare(String(b?.employeeId ?? ''), 'en-GB');
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function number(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function positive(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
